import {noop} from '@github-ui/noop'
import {GitPullRequestDraftIcon, StopIcon} from '@primer/octicons-react'
import {useAnalytics} from '@github-ui/use-analytics'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Flash, Tooltip} from '@primer/react'
import type {ViewerPrCreationLimitInfo} from '../../page-data/payloads/merge-box'
import CircleOcticon from '@github-ui/circle-octicon'

import {HEADER_ICON_SIZE} from '../../constants'
import {useMarkReadyForReviewMutation} from '../../hooks/mutations/use-mark-ready-for-review-mutation'
import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'
import {useId, useState} from 'react'

import styles from './DraftStateSection.module.css'

/**
 *
 * Renders if the pull request is in a Draft State
 */
export function DraftStateSection({
  viewerCanUpdate,
  viewerCanMarkReadyForReview,
  draftInclusionEnabled,
  helpUrl,
  viewerPrCreationLimitInfo,
}: {
  viewerCanUpdate: boolean
  viewerCanMarkReadyForReview: boolean
  draftInclusionEnabled: boolean
  helpUrl: string
  viewerPrCreationLimitInfo?: ViewerPrCreationLimitInfo | null
}) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const {mutate: markReadyForReviewMutation, isPending} = useMarkReadyForReviewMutation({
    onError: (e: Error) => {
      setErrorMessage(e.message)
    },
  })
  const {sendAnalyticsEvent} = useAnalytics()
  const draftStateSectionAriaId = useId()
  const canMarkReadyForReview = draftInclusionEnabled
    ? viewerCanMarkReadyForReview
    : !viewerPrCreationLimitInfo?.reached

  function handleMarkReadyForReview() {
    if (isPending) return

    markReadyForReviewMutation()
    sendAnalyticsEvent(
      'draft_state_section.mark_ready_for_review',
      'MERGEBOX_DRAFT_STATE_SECTION_MARK_READY_FOR_REVIEW_BUTTON',
    )
  }

  const url = `${helpUrl}/get-started/learning-about-github/access-permissions-on-github`

  return (
    <section
      aria-label="Draft state"
      aria-describedby={draftStateSectionAriaId}
      className="border-bottom color-border-subtle"
    >
      <MergeBoxSectionHeader
        headerId={draftStateSectionAriaId}
        title="This pull request is still a work in progress"
        subtitle={
          viewerCanUpdate ? (
            'Draft pull requests cannot be merged.'
          ) : (
            <span>
              Only those with <a href={url}>write access</a> to this repository can mark a draft pull request as ready
              for review.
            </span>
          )
        }
        icon={
          <CircleOcticon
            size={HEADER_ICON_SIZE}
            icon={() => <GitPullRequestDraftIcon size={16} />}
            className={styles.DraftStateCircleIcon}
          />
        }
        rightSideContent={
          viewerCanUpdate ? (
            !canMarkReadyForReview ? (
              <Tooltip direction="nw" text="Author has reached the open pull request limit">
                <Button inactive aria-disabled onClick={noop}>
                  Ready for review
                </Button>
              </Tooltip>
            ) : (
              <Button
                onClick={handleMarkReadyForReview}
                loading={isPending}
                loadingAnnouncement="Marking ready for review"
              >
                Ready for review
              </Button>
            )
          ) : undefined
        }
      >
        {errorMessage && (
          <Flash variant="danger" className={styles.DraftStateFlashMessage}>
            <StopIcon className={styles.DraftStateIcon} />
            {errorMessage}
          </Flash>
        )}
      </MergeBoxSectionHeader>
    </section>
  )
}
